import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalUser } from '../../../shared/domain/user';
import { LoggingService } from '../../../shared/logging.service';
import { firstValueFrom, interval, skipWhile, Subscription, timer } from 'rxjs';
import { AuthService } from '../../auth.service';
import { AppContextService } from '../../../shared/services/app-context.service';
import { FirebaseService } from '../../../firebase/firebase.service';
import firebase from 'firebase/app';
import { NgForm } from '@angular/forms';
import { touchForm } from '../../../shared/touch-form';
import { waitForTick } from '../../../shared/wait-for-tick';
import { finalize, take, tap, timeout } from 'rxjs/operators';

const TIMER_COUNT = 60;

enum LoginState {
  DEFAULT,
  OTP_ENTER,
}

@Component({
  selector: 'townip-login',
  templateUrl: './login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {

  public user: PortalUser;

  public isInvite: boolean;

  public isLoadingSession: boolean; // For the loading indicator

  public isLoggingIn: boolean; // The submit button loading indicator

  public newLogin: boolean;

  public isError: boolean;

  public loginEnabled = true;

  public reCaptchaVerifier: firebase.auth.RecaptchaVerifier;

  public reCaptchaVerifierForRetry: firebase.auth.RecaptchaVerifier;

  public loginStates = LoginState;

  public currentState = LoginState.DEFAULT;

  public otpError = false;

  public otpErrorMsg = '';

  public smsVerificationCode = '';

  public otpHints: firebase.auth.PhoneMultiFactorInfo;

  public resendDisabled = false;

  public timerCount = 0;

  public timerSub: Subscription;

  private userSubscription: Subscription;

  private connectedSubscription: Subscription;

  private smsVerificationId: string;

  private mfaResolver: firebase.auth.MultiFactorResolver;

  constructor(
    public appContext: AppContextService,
    private authService: AuthService,
    private loggingService: LoggingService,
    private router: Router,
    private route: ActivatedRoute,
    private firebaseService: FirebaseService,
  ) {

    // The login for hidden by default while we check for 'existing' session
    this.isLoadingSession = true;
    this.isInvite = false;
    this.isError = false;
    this.loginEnabled = !this.appContext.isAccountPortal();
  }

  public ngOnInit(): void {
    const token = this.route.snapshot.queryParams.token;
    if (!!token) {
      this.loggingService.info('This is an invite',  this.route.snapshot.queryParams);
      // you will not be connected on an invite, make sure to log out other users on
      // other tabs
      this.authService.logout();
      const user = this.route.snapshot.queryParams.user;

      this.loggingService.info('Invite user ', user, ' token ', token);
      this.authService.login(user, token, true)
        .catch((res) => {
          this.router.navigate(['/auth/token-error']);
        });
      return;
    }

    const oobCode = this.route.snapshot.queryParams.oobCode;
    if (!!oobCode) {
      this.loggingService.info('This is a password reset',  this.route.snapshot.queryParams);
      this.authService.setOobCode(oobCode);
      this.authService
        .logout()
        .then(() => {
          this.router.navigate(['../password-update', { hasCode: true }], { relativeTo: this.route });
        });
      return;
    }

    const customToken = this.route.snapshot.queryParams.cst;
    if (customToken) {
      // We don't need to emit the logout events because we are starting over
      // Force logout (in-case we have a previous instance)
      this.authService
        .logout(false)
        .then(() => {
          this.loginWithCustomToken(customToken);
        });
      return;
    }

    this.connect(this.authService.isConnected());
    this.connectedSubscription = this.authService.connectedEmitter.subscribe(connected => {
      this.connect(connected);
    });

    this.initRecaptchaVerifier();
  }

  private connect(connected: boolean): void {
    this.isLoadingSession = connected;
    if (connected) {
      setTimeout(() => {
        // This works in conjunction with authService.authenticate
        // The return URL is being set in there.
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        if (returnUrl) {
          const queries = JSON.parse(returnUrl);
          this.router.navigate(['/redirect'], { queryParams: queries });
          return;
        }

        this.router.navigate(['/dashboard/landing']);
      });
    }
  }

  public async login(username: string, password: string): Promise<void> {
    if (this.isLoggingIn) {
      return;
    }

    this.clearOtpErrors();

    this.isLoggingIn = true;
    this.newLogin = true;
    this.isError = false;
    this.otpError = false;

    // ----------- Recaptcha Handling --------------
    await this.reCaptchaVerifier.verify();
    const solverInstance = this.firebaseService
      .recaptchaSolved
      .pipe(
        skipWhile(val => val === false),
        timeout({ first: 10000 }) // 10 seconds. If no response from captcha, error out
      );
    try {
      await firstValueFrom(solverInstance);
    } catch (er) {
      this.isLoggingIn = false;
      this.isError = true;
      return;
    }
    // ---------------------------------------------

    try {
      await this.authService.login(username.trim(), password, this.isInvite);
    } catch (e) {
      if (e.code === 'auth/multi-factor-auth-required') {
        // This will always be only phone number SMS.
        // If we update the MFA modes, this needs to be updated to handle that.
        this.mfaResolver = e.resolver;
        this.otpHints = e.resolver.hints[0];

        this.firebaseService
          .sendMfaCode(e.resolver, this.reCaptchaVerifier)
          .then(async (verificationId) => {
            this.currentState = LoginState.OTP_ENTER;
            this.smsVerificationId = verificationId;

            await waitForTick();
            this.reCaptchaVerifierForRetry = this.firebaseService
              .initInvisibleCaptchaVerifier('btn-resend-code');
          })
          .catch(e => {
            console.error(e);
            this.otpError = true;
            this.otpErrorMsg = this.firebaseService.translateErrorCode(e.code, e.message);
          })
          .finally(() => {
            // Ideally, we should wait for the recaptcha verification to complete
            // to enable the login button again, but we are simplifying how we:
            // 1 - Prevent user from clicking this too fast
            // 2 - Waiting for the recaptcha solver to complete.
            // We could probably hook into the verifier callback, but that would mean we will need
            // to device some way to access that from here which can make this extra complicated.
            // If this causes a problem down the road then we'll do the latter.
            setTimeout(() => {
              this.isLoggingIn = false;
            }, 2500);
          });

        return;
      }

      // Other errors
      this.isLoggingIn = false;
      this.isError = true;
    }
  }

  public async verifyMfaLogin(form: NgForm): Promise<void> {
    touchForm(form);
    if (form.invalid) {
      return;
    }

    try {
      this.isLoggingIn = true;
      await this.firebaseService
        .verifyMfaSignIn(this.mfaResolver, this.smsVerificationId, this.smsVerificationCode);

    } catch (e) {
      this.isLoggingIn = false;
      this.otpError = true;
      this.otpErrorMsg = this.firebaseService.translateErrorCode(e.code, e.message);

      console.error(e);
    }
  }

  public cancelMfa(): void {
    this.isLoggingIn = false;
    this.newLogin = true;
    this.isError = false;

    // MFA related data
    this.otpError = false;
    this.otpErrorMsg = '';
    this.mfaResolver = null;
    this.smsVerificationCode = '';
    this.smsVerificationId = '';
    this.otpHints = null;

    this.initRecaptchaVerifier();

    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    this.currentState = LoginState.DEFAULT;
  }

  public ngOnDestroy(): void {
    if (!!this.userSubscription) {
      this.userSubscription.unsubscribe();
    }

    if (!!this.connectedSubscription) {
      this.connectedSubscription.unsubscribe();
    }

    if (this.reCaptchaVerifier) {
      this.reCaptchaVerifier.clear();
    }

    if (this.reCaptchaVerifierForRetry) {
      this.reCaptchaVerifierForRetry.clear();
    }
  }

  public async resendCode(btn: HTMLButtonElement): Promise<void> {
    if (this.resendDisabled) {
      return;
    }

    this.clearOtpErrors();
    this.initRetryTimer();

    try {
      this.smsVerificationId = await this.firebaseService
        .sendMfaCode(this.mfaResolver, this.reCaptchaVerifierForRetry);

      // For some reason - recaptcha removes the disabled state.
      // Manually add it. (not sure of side effects).
      // Also - it has to be here, otherwise it will not work.
      btn.disabled = true;

    } catch (e) {
      this.otpError = true;
      this.otpErrorMsg = this.firebaseService.translateErrorCode(e.code, e.message);

      if (e.code === 'auth/invalid-multi-factor-session') {
        this.cancelMfa();
        this.otpError = true;
        this.otpErrorMsg = 'MFA Session expired';
      }

      console.error(e);
    }
  }

  private loginWithCustomToken(token: string): void {
    this.isLoadingSession = true;
    this.isError = false;
    this.authService.loginWithToken(token)
      .then(() => {
        this.authService.authenticate();
        return this.router.navigate(['/dashboard/landing']);
      })
      .then(() => {
        this.isLoadingSession = false;
      })
      .catch(e => {
        this.isLoadingSession = true;
        this.isError = true;
        console.error(e);
      });
  }

  private initRetryTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
    }

    this.resendDisabled = true;
    this.timerCount = TIMER_COUNT;

    this.timerSub = interval(1000)
      .pipe(
        tap(() => {
          this.timerCount --;
        }),
        take(TIMER_COUNT),
        finalize(() => {
          this.resendDisabled = false;
        })
      )
      .subscribe();
  }

  private async initRecaptchaVerifier(wait: boolean = true): Promise<void> {
    if (wait) {
      await waitForTick();
    }

    this.reCaptchaVerifier = this.firebaseService.initInvisibleCaptchaVerifier('btn-mfa-login');
  }

  private clearOtpErrors(): void {
    this.otpError = false;
    this.otpErrorMsg = '';
  }
}
